import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "./Button";
import Checkbox from "./Checkbox";
import LoadingSpinner from "./LoadingSpinner";
import {
  createFleetReport,
  fetchElectricRateOptions,
  fetchFleetElectricRate,
  getAllUnit,
  getFleetReportStatus,
} from "../../lib/api";
import { getEffectiveElectricRate, getUniqueElectricRateOptions } from "../../utils/electricRate";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import Label from "./Label";
import { trackEvent } from "../../lib/analytics";
import { AnalyticsSchemas, ReportKind, ReportScope, SearchContext } from "../../lib/analyticsEvents";

const DATA_REPORT_FIELDS = [
  { id: "CONNECTION_STATUS", label: "Connection Status" },
  { id: "POWER_STATUS", label: "Power Status" },
  { id: "MODE", label: "Mode" },
  { id: "SETPOINT", label: "Setpoint" },
  { id: "INDOOR_TEMP", label: "Indoor Temp" },
  { id: "OUTDOOR_TEMP", label: "Outdoor Temp" },
  { id: "ENERGY_CONSUMPTION", label: "Energy Consumption (kWh/BTU)" },
  { id: "COST", label: "Cost", requiresElectricRate: true },
  { id: "RELATIVE_HUMIDITY", label: "Avg Relative Humidity (%)" },
  { id: "AVG_AMPERAGE", label: "Avg Amperage (A)" },
  { id: "MAX_AMPERAGE", label: "Max Amperage (A)" },
  { id: "AVG_POWER_DRAW", label: "Avg Power Draw (W)" },
  { id: "MAX_POWER_DRAW", label: "Max Power Draw (W)" },
  { id: "FAN_STATUS", label: "Fan Status" },
  { id: "FAN_SPEED", label: "Fan Speed" },
  { id: "COMPRESSOR_STATUS", label: "Compressor Status" },
  { id: "AVG_COMPRESSOR_RPM", label: "Avg Compressor RPM" },
  { id: "MAX_COMPRESSOR_RPM", label: "Max Compressor RPM" },
];

const GenerateReportModal = ({
  isOpen,
  onClose,
  selectedFleet,
  allPodData,
  showToast = () => {},
  prefillData = null,
}) => {
  const now = useMemo(() => new Date(), []);

  const MIN_YEAR = 2025;
  const MIN_MONTH_INDEX = 7; // August (0-based)

  const dateToIso = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  const dateToAmericanDateFormat = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${m}-${d}-${y}`;
  };
  const minDate = useMemo(() => new Date(MIN_YEAR, MIN_MONTH_INDEX, 1), []);
  const today = useMemo(() => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    d.setHours(0, 0, 0, 0);
    return d;
  }, [now]);

  const [reportFleetUnid, setReportFleetUnid] = useState("");
  const [reportStep, setReportStep] = useState("intro");
  const [reportType, setReportType] = useState("ZONE_WIDE");
  const [reportKind, setReportKind] = useState(ReportKind.alerts);
  const [reportStartDate, setReportStartDate] = useState(null);
  const [reportEndDate, setReportEndDate] = useState(null);
  const [isDateCalendarOpen, setIsDateCalendarOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [hasConfiguredElectricRate, setHasConfiguredElectricRate] = useState(false);
  const [electricRateFleetUnid, setElectricRateFleetUnid] = useState(null);
  const [reportUnits, setReportUnits] = useState([]);
  const [reportUnitsLoading, setReportUnitsLoading] = useState(false);
  const [reportUnitsPage, setReportUnitsPage] = useState(1);
  const [reportUnitsTotal, setReportUnitsTotal] = useState(0);
  const REPORT_UNITS_PAGE_SIZE = 10;
  const [reportUnitsSearch, setReportUnitsSearch] = useState("");
  const [selectedReportUnits, setSelectedReportUnits] = useState([]);
  const [reportUnitsUnid, setReportUnitsUnid] = useState(null);
  const [reportGenerationStatus, setReportGenerationStatus] = useState("idle"); // idle | loading | ready
  const [reportDownloadUrl, setReportDownloadUrl] = useState(null);
  const reportGenerationTimeoutRef = useRef(null);
  const activeReportIdRef = useRef(null);
  const trackedDownloadUrlRef = useRef(null);
  const [reportUnitsHasMore, setReportUnitsHasMore] = useState(false);
  const latestSearchTermRef = useRef("");
  const reportFleetUnidRef = useRef("");

  const steps = useMemo(() => {
    const arr = ["intro"];
    if (reportType === "UNIT_SPECIFIC") arr.push("units");
    if (reportKind === ReportKind.data) arr.push("fields");
    arr.push("dateRange", "result");
    return arr;
  }, [reportType, reportKind]);

  const currentStepIndex = steps.indexOf(reportStep);
  const totalSteps = steps.length;

  const addOneDay = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    return d;
  };

  const handleDownloadReport = () => {
    if (!reportDownloadUrl) return;
    if (trackedDownloadUrlRef.current !== reportDownloadUrl) {
      trackedDownloadUrlRef.current = reportDownloadUrl;
      trackEvent(AnalyticsSchemas.csvReportDownload, {
        unid: reportFleetUnid,
        report_kind: reportKind,
        report_scope: reportType === "UNIT_SPECIFIC" ? ReportScope.unit : ReportScope.zone,
        start_date: dateToIso(reportStartDate),
        end_date: dateToIso(addOneDay(reportEndDate)),
        fields: reportKind === ReportKind.data ? selectedFields : null,
      });
    }
    window.open(reportDownloadUrl);
  };

  const searchReportUnits = () => {
    const trimmedSearch = reportUnitsSearch.trim();
    if (trimmedSearch) {
      trackEvent(AnalyticsSchemas.webSearch, {
        search_context: SearchContext.reportUnits,
        term_length: trimmedSearch.length,
      });
    }
    fetchReportUnits(1, reportUnitsSearch, true);
  };

  const buildReportRequestPayload = () => {
    const startDate = reportStartDate;
    const endDate = addOneDay(reportEndDate);

    return {
      startDate: dateToIso(startDate),
      endDate: dateToIso(endDate),
      serialNumbers: reportType === "UNIT_SPECIFIC" ? selectedReportUnits : null,
      reportKind,
      fields: reportKind === ReportKind.data ? selectedFields : null,
    };
  };

  const resetState = useCallback(() => {
    const defaultUnid = prefillData?.fleetUnid || selectedFleet?.unid || allPodData?.zones?.[0]?.unid || "";
    const hasPrefilledUnit = Boolean(prefillData?.unitSerialNumber);
    const nextReportType = prefillData?.reportType || (hasPrefilledUnit ? "UNIT_SPECIFIC" : "ZONE_WIDE");
    const nextReportKind = prefillData?.reportKind || ReportKind.alerts;

    setReportFleetUnid(defaultUnid);
    setReportType(nextReportType);
    setReportKind(nextReportKind);
    setReportStep("intro");
    setSelectedReportUnits(hasPrefilledUnit ? [prefillData.unitSerialNumber] : []);
    setReportUnitsPage(1);
    setReportUnitsTotal(0);
    setReportUnitsSearch("");
    setReportUnitsUnid(null);
    setReportUnitsLoading(false);
    setReportUnitsHasMore(false);
    setReportStartDate(null);
    setReportEndDate(null);
    setIsDateCalendarOpen(false);
    setSelectedFields([]);
    setHasConfiguredElectricRate(false);
    setElectricRateFleetUnid(null);
    setReportGenerationStatus("idle");
    setReportDownloadUrl(null);
    setReportUnits([]);
    activeReportIdRef.current = null;
    if (reportGenerationTimeoutRef.current) {
      clearTimeout(reportGenerationTimeoutRef.current);
    }
  }, [allPodData?.zones, prefillData, selectedFleet?.unid]);

  useEffect(() => {
    if (isOpen) {
      resetState();
    } else {
      activeReportIdRef.current = null;
      if (reportGenerationTimeoutRef.current) {
        clearTimeout(reportGenerationTimeoutRef.current);
      }
    }
  }, [isOpen, resetState]);

  useEffect(
    () => () => {
      if (reportGenerationTimeoutRef.current) {
        clearTimeout(reportGenerationTimeoutRef.current);
      }
      activeReportIdRef.current = null;
    },
    []
  );

  useEffect(() => {
    latestSearchTermRef.current = reportUnitsSearch;
  }, [reportUnitsSearch]);

  useEffect(() => {
    reportFleetUnidRef.current = reportFleetUnid;
  }, [reportFleetUnid]);

  useEffect(() => {
    if (!hasConfiguredElectricRate) {
      setSelectedFields((prev) => (prev.includes("COST") ? prev.filter((id) => id !== "COST") : prev));
    }
  }, [hasConfiguredElectricRate]);

  const validateDateRange = () => {
    if (!reportStartDate || !reportEndDate) return "Please select both start and end dates";
    const start = new Date(reportStartDate);
    const end = new Date(reportEndDate);
    if (end < start) return "Start date must be on or before end date";
    return null;
  };

  const dateRangeError = validateDateRange();

  const getSelectedFleetName = () => {
    const fleets = allPodData?.zones || [];
    return fleets.find((fleet) => fleet.unid === reportFleetUnid)?.name || selectedFleet?.name || "N/A";
  };

  const formatPeriod = () => {
    if (reportStartDate && reportEndDate) {
      return `${dateToAmericanDateFormat(reportStartDate)} to ${dateToAmericanDateFormat(reportEndDate)}`;
    }
    return "N/A";
  };

  const getSubHeader = () => {
    if (reportStep === "intro") return "Download reports based on your selection.";
    if (reportStep === "units") return "Select units to include";
    if (reportStep === "fields") return "Choose the data fields to include in your report";
    if (reportStep === "dateRange") return "Set the date range for your report";
    if (reportStep === "result") return "Your report is ready";
    return "";
  };

  const fetchReportUnits = useCallback(
    async (page = 1, searchTerm = "", shouldReset = false) => {
      if (!reportFleetUnid) {
        setReportUnits([]);
        setReportUnitsTotal(0);
        setReportUnitsHasMore(false);
        return;
      }
      if (reportUnitsLoading && !shouldReset) return;
      if (shouldReset || page === 1) {
        setReportUnits([]);
        setReportUnitsTotal(0);
        setReportUnitsHasMore(false);
        setReportUnitsPage(1);
      }
      const activeFleetUnid = reportFleetUnid;
      const activeSearchTerm = searchTerm;
      setReportUnitsLoading(true);
      try {
        const response = await getAllUnit({
          pageNumber: page,
          pageSize: REPORT_UNITS_PAGE_SIZE,
          unid: reportFleetUnid,
          search: activeSearchTerm,
        });
        const units = response?.data?.data?.units || [];
        const total = response?.data?.data?.count || units.length;

        if (activeFleetUnid !== reportFleetUnidRef.current || activeSearchTerm !== latestSearchTermRef.current) {
          return;
        }

        setReportUnits((prev) => (shouldReset || page === 1 ? units : [...prev, ...units]));
        setReportUnitsTotal(total);
        setReportUnitsHasMore(page * REPORT_UNITS_PAGE_SIZE < total);
        setReportUnitsPage(page);
        setReportUnitsUnid(reportFleetUnid);
      } catch (error) {
        console.error("Error fetching units for report:", error);
        showToast("Failed to load units for report", "failed");
        setReportUnits([]);
        setReportUnitsTotal(0);
        setReportUnitsHasMore(false);
      } finally {
        setReportUnitsLoading(false);
      }
    },
    [reportFleetUnid, showToast, reportUnitsLoading]
  );

  useEffect(() => {
    if (
      isOpen &&
      reportStep === "units" &&
      reportType === "UNIT_SPECIFIC" &&
      reportUnitsUnid !== reportFleetUnid
    ) {
      fetchReportUnits(1, latestSearchTermRef.current, true);
    }
  }, [
    isOpen,
    reportStep,
    reportType,
    fetchReportUnits,
    reportUnitsUnid,
    reportFleetUnid,
  ]);

  const fetchZoneElectricRate = useCallback(async (unid) => {
    if (!unid) return;
    try {
      const [rateResponse, optionsResponse] = await Promise.all([
        fetchFleetElectricRate({ unid }),
        fetchElectricRateOptions(),
      ]);
      if (unid !== reportFleetUnidRef.current) return;
      const electricRate = rateResponse?.data?.data?.electricRate || null;
      const options = getUniqueElectricRateOptions(optionsResponse?.data?.data?.options || []);
      const effectiveRate = getEffectiveElectricRate(electricRate, options);
      setHasConfiguredElectricRate(Number.isFinite(effectiveRate));
    } catch (error) {
      console.error("Error fetching electric rate for report:", error);
      if (unid !== reportFleetUnidRef.current) return;
      setHasConfiguredElectricRate(false);
    } finally {
      if (unid === reportFleetUnidRef.current) {
        setElectricRateFleetUnid(unid);
      }
    }
  }, []);

  useEffect(() => {
    if (
      isOpen &&
      reportStep === "fields" &&
      reportFleetUnid &&
      electricRateFleetUnid !== reportFleetUnid
    ) {
      fetchZoneElectricRate(reportFleetUnid);
    }
  }, [isOpen, reportStep, reportFleetUnid, electricRateFleetUnid, fetchZoneElectricRate]);

  const toggleUnitSelection = (serialNumber) => {
    setSelectedReportUnits((prev) =>
      prev.includes(serialNumber)
        ? prev.filter((sn) => sn !== serialNumber)
        : [...prev, serialNumber]
    );
  };

  const selectAllUnits = () => {
    setSelectedReportUnits((prev) =>
      Array.from(new Set([...prev, ...reportUnits.map((unit) => unit.serialNumber)]))
    );
  };

  const clearAllUnits = () => setSelectedReportUnits([]);
  const handleLoadMoreUnits = useCallback(() => {
    if (reportUnitsLoading || !reportUnitsHasMore) return;
    const nextPage = reportUnitsPage + 1;
    fetchReportUnits(nextPage, reportUnitsSearch);
  }, [fetchReportUnits, reportUnitsHasMore, reportUnitsLoading, reportUnitsPage, reportUnitsSearch]);

  const toggleField = (fieldId) => {
    setSelectedFields((prev) =>
      prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : [...prev, fieldId]
    );
  };

  const selectableFieldIds = DATA_REPORT_FIELDS.filter((field) => {
    const isDisabled = field.requiresElectricRate && !hasConfiguredElectricRate;
    return !isDisabled;
  }).map((field) => field.id);

  const selectAllFields = () => setSelectedFields(selectableFieldIds);
  const clearAllFields = () => setSelectedFields([]);

  const pollReportStatus = useCallback(
    async ({ reportId, fleetUnid }) => {
      try {
        const response = await getFleetReportStatus({ unid: fleetUnid, reportId });
        const reportData = response?.data?.data;

        if (activeReportIdRef.current !== reportId) {
          return;
        }

        if (!reportData) {
          throw new Error("Invalid report status response");
        }

        if (reportData.status === "success") {
          activeReportIdRef.current = null;
          setReportGenerationStatus("ready");
          setReportDownloadUrl(reportData.downloadUrl || null);
          setReportStep("result");
          showToast("Report generated successfully!", "success");
          return;
        }

        if (reportData.status === "failed") {
          activeReportIdRef.current = null;
          setReportGenerationStatus("idle");
          showToast("Report generation failed", "failed");
          return;
        }

        reportGenerationTimeoutRef.current = setTimeout(() => {
          pollReportStatus({ reportId, fleetUnid });
        }, 5000);
      } catch (error) {
        if (activeReportIdRef.current !== reportId) {
          return;
        }
        activeReportIdRef.current = null;
        setReportGenerationStatus("idle");
        showToast("Failed to generate report", "failed");
      }
    },
    [showToast]
  );

  const handleGenerateReport = async () => {
    if (!reportFleetUnid) {
      showToast("Please select a zone to generate a report", "failed");
      return;
    }
    setReportGenerationStatus("loading");
    setReportDownloadUrl(null);
    if (reportGenerationTimeoutRef.current) {
      clearTimeout(reportGenerationTimeoutRef.current);
    }

    const requestPayload = buildReportRequestPayload();
    try {
      const response = await createFleetReport({ unid: reportFleetUnid }, requestPayload);
      const reportId = response?.data?.data?.id;

      if (!reportId) {
        throw new Error("Missing report id");
      }

      activeReportIdRef.current = reportId;
      pollReportStatus({
        reportId,
        fleetUnid: reportFleetUnid,
      });
    } catch (error) {
      activeReportIdRef.current = null;
      setReportGenerationStatus("idle");
      showToast("Failed to generate report", "failed");
    }
  };

  const handleReportNext = () => {
    if (reportStep === "dateRange") {
      handleGenerateReport();
      return;
    }
    const nextStep = steps[currentStepIndex + 1];
    if (nextStep) {
      setReportStep(nextStep);
    }
  };

  const handleReportBack = () => {
    if (reportGenerationStatus === "loading") return;
    if (currentStepIndex > 0) {
      setReportStep(steps[currentStepIndex - 1]);
      return;
    }
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const isLoadingGenerate = reportGenerationStatus === "loading" && reportStep === "dateRange";
  const isCheckingElectricRate = electricRateFleetUnid !== reportFleetUnid;
  const isNextDisabled =
    reportGenerationStatus === "loading" ||
    (reportStep === "intro" && !reportFleetUnid) ||
    (reportStep === "units" && selectedReportUnits.length === 0) ||
    (reportStep === "fields" && (isCheckingElectricRate || selectedFields.length === 0)) ||
    (reportStep === "dateRange" && !!dateRangeError);
  const downloadButtonDisabled = !reportDownloadUrl;

  return (
    <div
      className="overlayHolder bg-black/30 dark:bg-black/60 fixed inset-0 flex justify-center items-center z-[999] px-4"
      data-testid="generate-report-modal"
    >
      <div
        className={`modalHlder bg-white dark:bg-darkThemeModalBg dark:shadow-darkThemeModalShadow rounded-2xl border border-black/10 w-full max-w-3xl mx-auto p-0 ${
          isDateCalendarOpen && reportStep === "dateRange" ? "overflow-visible" : "overflow-hidden"
        }`}
      >
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-[#14171F] dark:text-white text-2xl font-semibold leading-7">Generate Report</h2>
            {!isLoadingGenerate && getSubHeader() && (
              <p className="text-sm text-[#4B5563] dark:text-white/70">{getSubHeader()}</p>
            )}
          </div>
          <Button
            aria-label="Close generate report modal"
            data-testid="generate-report-modal-close"
            className="text-[#7B8089] hover:text-[#14171F] dark:text-white/70 dark:hover:text-white transition-colors p-1"
            onClick={onClose}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Button>
        </div>

        {/* Body sections */}
        {isLoadingGenerate && (
          <div className="border-t border-b border-[#E8E9ED] dark:border-darkThemeBorder px-6 py-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-center">
              <LoadingSpinner size="medium" />
              <p className="text-lg font-semibold text-[#14171F] dark:text-white">Generating...</p>
              <p className="text-sm text-[#4B5563] dark:text-white/70">Please keep this window open.</p>
            </div>
          </div>
        )}

        {!isLoadingGenerate && reportStep === "intro" && (
          <div className="border-t border-b border-[#E8E9ED] dark:border-darkThemeBorder px-6 py-6 max-h-[60vh] overflow-y-auto">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-3">
                <p className="text-[#14171F] dark:text-white text-base font-semibold leading-6">1. Zone Selection</p>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-semibold text-[#14171F] dark:text-white leading-5">
                    Zone<span className="text-[#ff8c65]">*</span>
                  </Label>
                  <div className="relative">
                    <select
                      data-testid="generate-report-modal-zone-select"
                      className="border border-[#E1E5EA] dark:border-darkThemeBorder bg-white dark:bg-darkThemeModalBg text-sm text-[#2F343A] dark:text-white rounded-lg h-12 pl-3 pr-10 appearance-none w-full focus:outline-none focus:ring-2 focus:ring-theme shadow-appShadow dark:[&_option]:bg-white dark:[&_option]:text-[#14171F]"
                      value={reportFleetUnid}
                      onChange={(e) => {
                        setReportFleetUnid(e.target.value);
                        setReportUnits([]);
                        setReportUnitsTotal(0);
                        setReportUnitsHasMore(false);
                        setReportUnitsPage(1);
                        setReportUnitsUnid(null);
                        setSelectedReportUnits([]);
                        setElectricRateFleetUnid(null);
                        setHasConfiguredElectricRate(false);
                      }}
                    >
                      {!reportFleetUnid && <option value="">Choose a zone...</option>}
                      {allPodData?.zones?.map((fleet) => (
                        <option key={fleet.unid} value={fleet.unid}>
                          {fleet.name}
                        </option>
                      ))}
                    </select>
                    <svg
                      aria-hidden="true"
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2F343A] dark:text-white"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path d="M3.5 6l4.5 4.5L12.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <p className="text-[#14171F] dark:text-white text-base font-semibold leading-6">2. Report Scope</p>
                <p className="sr-only">Choose the scope of the report you want to generate:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                  {
                    id: "ZONE_WIDE",
                    title: "Zone-Wide Report",
                    description: "Generate a comprehensive report for all units in the zone",
                  },
                  {
                    id: "UNIT_SPECIFIC",
                    title: "Unit-Specific Report",
                    description: "Select specific units to include in your report",
                  },
                ].map((option) => {
                  const isSelected = reportType === option.id;
                  return (
                    <Button
                      key={option.id}
                      data-testid={`generate-report-modal-scope-${option.id}`}
                      onClick={() => {
                        setReportType(option.id);
                        if (option.id === "UNIT_SPECIFIC") {
                          setSelectedReportUnits([]);
                        }
                      }}
                      className={`w-full text-left border rounded-xl px-4 py-4 transition-colors ${
                        isSelected
                          ? "border-theme bg-[#FFF1E7] dark:bg-darkThemeDropdownItemBg"
                          : "border-[#E1E5EA] dark:border-darkThemeBorder bg-white dark:bg-darkThemeModalBg hover:border-theme/70"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                            isSelected ? "border-theme" : "border-[#C6CBD4] bg-white dark:bg-darkThemeModalBg"
                          }`}
                        >
                          <span
                            className={`block h-5 w-5 rounded-full transition-colors ${
                              isSelected ? "bg-theme" : "bg-transparent"
                            }`}
                          />
                        </span>
                        <div className="flex flex-col gap-1">
                          <p className="text-[#14171F] dark:text-white text-base font-semibold leading-6">
                            {option.title}
                          </p>
                          <p className="text-[#4B4F55] dark:text-white/80 text-sm leading-5">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </Button>
                  );
                })}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <p className="text-[#14171F] dark:text-white text-base font-semibold leading-6">3. Report Kind</p>
                <p className="sr-only">Choose whether to download an alerts report or a data report:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                  {
                    id: ReportKind.alerts,
                    title: "Alerts Report",
                    description: "Generate a report of alerts triggered during the selected period",
                  },
                  {
                    id: ReportKind.data,
                    title: "Data Report",
                    description: "Generate a report of unit data fields you select",
                  },
                ].map((option) => {
                  const isSelected = reportKind === option.id;
                  return (
                    <Button
                      key={option.id}
                      data-testid={`generate-report-modal-kind-${option.id}`}
                      onClick={() => setReportKind(option.id)}
                      className={`w-full text-left border rounded-xl px-4 py-4 transition-colors ${
                        isSelected
                          ? "border-theme bg-[#FFF1E7] dark:bg-darkThemeDropdownItemBg"
                          : "border-[#E1E5EA] dark:border-darkThemeBorder bg-white dark:bg-darkThemeModalBg hover:border-theme/70"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                            isSelected ? "border-theme" : "border-[#C6CBD4] bg-white dark:bg-darkThemeModalBg"
                          }`}
                        >
                          <span
                            className={`block h-5 w-5 rounded-full transition-colors ${
                              isSelected ? "bg-theme" : "bg-transparent"
                            }`}
                          />
                        </span>
                        <div className="flex flex-col gap-1">
                          <p className="text-[#14171F] dark:text-white text-base font-semibold leading-6">
                            {option.title}
                          </p>
                          <p className="text-[#4B4F55] dark:text-white/80 text-sm leading-5">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </Button>
                  );
                })}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLoadingGenerate && reportStep === "units" && (
          <div className="border-t border-b border-[#E8E9ED] dark:border-darkThemeBorder px-6 py-6 max-h-[60vh] overflow-hidden">
            <div className="flex flex-col gap-4 h-full min-h-0">
              <div className="flex items-center justify-between">
                <p className="text-[#14171F] dark:text-white text-base font-semibold leading-6">
                  Select the unit(s) to include in your report:
                </p>
                <div className="flex items-center gap-3 text-sm font-semibold">
                  <Button
                    data-testid="generate-report-modal-units-select-all"
                    className={`text-theme hover:text-theme/80 ${reportUnits.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                    disabled={reportUnits.length === 0}
                    onClick={selectAllUnits}
                  >
                    Select All
                  </Button>
                  <span className="text-[#c3c7cf] dark:text-white/40">|</span>
                  <Button
                    data-testid="generate-report-modal-units-clear-all"
                    className={`text-theme hover:text-theme/80 ${selectedReportUnits.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                    disabled={selectedReportUnits.length === 0}
                    onClick={clearAllUnits}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="w-full sm:w-72 flex items-center gap-2">
                  <input
                    type="text"
                    value={reportUnitsSearch}
                    onChange={(e) => setReportUnitsSearch(e.target.value)}
                    placeholder="Search units..."
                    data-testid="generate-report-modal-units-search-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        searchReportUnits();
                      }
                    }}
                    className="w-full border border-[#E1E5EA] dark:border-darkThemeBorder rounded-lg h-10 px-3 text-sm text-[#2F343A] dark:text-white bg-white dark:bg-transparent focus:outline-none focus:ring-2 focus:ring-theme"
                  />
                  <Button
                    onClick={searchReportUnits}
                    data-testid="generate-report-modal-units-search-button"
                    className="h-10 px-3 bg-[#FF8C65] hover:bg-[#f5784d] text-white text-sm font-semibold rounded-lg"
                  >
                    Search
                  </Button>
                </div>
                <div className="text-sm text-[#4B4F55] dark:text-white/80">
                  Selected {selectedReportUnits.length} of {reportUnitsTotal || reportUnits.length} units
                </div>
              </div>
              <div className="border border-[#E1E5EA] dark:border-darkThemeBorder rounded-xl overflow-hidden flex-1 min-h-[200px] bg-white dark:bg-darkThemeModalBg">
                <div
                  className="max-h-[50vh] overflow-y-auto divide-y divide-[#E1E5EA] dark:divide-darkThemeBorder pb-4 bg-white dark:bg-darkThemeModalBg"
                  onScroll={(e) => {
                    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                    if (scrollHeight - scrollTop - clientHeight < 120) {
                      handleLoadMoreUnits();
                    }
                  }}
                >
                  {reportUnitsLoading && reportUnits.length === 0 ? (
                    <div className="p-6 text-sm text-[#4B4F55] dark:text-white/80 flex flex-col items-center justify-center gap-2">
                      <LoadingSpinner size="small" />
                      <span>Loading units...</span>
                    </div>
                  ) : reportUnits.length === 0 ? (
                    <div className="p-4 text-sm text-[#4B4F55] dark:text-white/80">No units found for this fleet.</div>
                  ) : (
                    <>
                      {reportUnits.map((unit) => {
                        const isChecked = selectedReportUnits.includes(unit.serialNumber);
                        return (
                          <Checkbox
                            key={unit.serialNumber}
                            checked={isChecked}
                            onChange={() => toggleUnitSelection(unit.serialNumber)}
                            data-testid={`generate-report-modal-unit-${unit.serialNumber}`}
                            className="w-full px-4 py-3 hover:bg-[#F9FAFB] dark:hover:bg-darkThemeModalBg items-center"
                            label={
                              <span className="flex flex-col">
                                <span
                                  className="text-[#14171F] dark:text-white text-base font-semibold leading-6 block w-full min-w-0 truncate"
                                  title={unit.metadata?.name || unit.serialNumber}
                                >
                                  {unit.metadata?.name || unit.serialNumber}
                                </span>
                                <span className="text-sm text-[#4B4F55] dark:text-white/80 mt-1">
                                  S/N: {unit.serialNumber}
                                </span>
                              </span>
                            }
                          />
                        );
                      })}
                      {reportUnitsLoading && reportUnits.length > 0 && (
                        <div className="p-3 text-center text-sm text-[#4B4F55] dark:text-white/80">Loading more units…</div>
                      )}
                      {!reportUnitsLoading && reportUnitsHasMore && (
                        <div className="p-3 text-center text-xs text-[#4B4F55] dark:text-white/80">
                          Scroll to load more units
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {!isLoadingGenerate && reportStep === "fields" && (
          <div className="border-t border-b border-[#E8E9ED] dark:border-darkThemeBorder px-6 py-6 max-h-[60vh] overflow-y-auto">
            {isCheckingElectricRate ? (
              <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                <LoadingSpinner size="medium" />
                <p className="text-sm text-[#4B5563] dark:text-white/70">Checking energy rate configuration…</p>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[#14171F] dark:text-white text-base font-semibold leading-6">
                      Select the fields to include in your report:
                    </p>
                    <div className="flex items-center gap-3 text-sm font-semibold">
                      <Button
                        data-testid="generate-report-modal-fields-select-all"
                        className={`text-theme hover:text-theme/80 ${
                          selectedFields.length >= selectableFieldIds.length ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                        disabled={selectedFields.length >= selectableFieldIds.length}
                        onClick={selectAllFields}
                      >
                        Select All
                      </Button>
                      <span className="text-[#c3c7cf] dark:text-white/40">|</span>
                      <Button
                        data-testid="generate-report-modal-fields-clear-all"
                        className={`text-theme hover:text-theme/80 ${selectedFields.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                        disabled={selectedFields.length === 0}
                        onClick={clearAllFields}
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  <div className="border border-[#E1E5EA] dark:border-darkThemeBorder rounded-xl overflow-hidden divide-y divide-[#E1E5EA] dark:divide-darkThemeBorder bg-white dark:bg-darkThemeModalBg">
                    {DATA_REPORT_FIELDS.map((field) => {
                      const isDisabled = field.requiresElectricRate && !hasConfiguredElectricRate;
                      return (
                        <Checkbox
                          key={field.id}
                          checked={selectedFields.includes(field.id)}
                          disabled={isDisabled}
                          onChange={() => toggleField(field.id)}
                          data-testid={`generate-report-modal-field-${field.id}`}
                          className="w-full px-4 py-3 hover:bg-[#F9FAFB] dark:hover:bg-darkThemeModalBg items-center"
                          label={
                            <span className="flex flex-col">
                              <span className={`text-[#14171F] dark:text-white text-base font-semibold leading-6 ${isDisabled ? "opacity-60" : ""}`}>
                                {field.label}
                              </span>
                              {isDisabled && (
                                <span className="text-sm text-[#4B4F55] dark:text-white/80 mt-1">
                                  Configure an energy rate for this zone to include cost in your report.
                                </span>
                              )}
                            </span>
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!isLoadingGenerate && reportStep === "dateRange" && (
          <div
            className={`border-t border-b border-[#E8E9ED] dark:border-darkThemeBorder px-6 py-6 max-h-[60vh] ${
              isDateCalendarOpen ? "overflow-visible" : "overflow-y-auto"
            }`}
          >
            <div className="flex flex-col gap-6">
              <div className="border border-[#E1E5EA] dark:border-darkThemeBorder rounded-xl p-4 bg-[#F9FAFB] dark:bg-darkThemeModalBg">
                <p className="text-[#14171F] dark:text-white text-base font-semibold leading-6 mb-4">Report Period</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-semibold text-[#14171F] dark:text-white">Start Date</Label>
                    <DatePicker
                      value={reportStartDate}
                      onChange={(date) => {
                        if (!date) {
                          setReportStartDate(null);
                          return;
                        }
                        const clamped = date < minDate ? minDate : date > today ? today : date;
                        setReportStartDate(clamped);
                      }}
                      minDate={minDate}
                      maxDate={today}
                      format="MM-dd-y"
                      dayPlaceholder="DD"
                      monthPlaceholder="MM"
                      yearPlaceholder="YYYY"
                      className="w-full dark-date-picker"
                      clearIcon={null}
                      calendarIcon={null}
                      onCalendarOpen={() => setIsDateCalendarOpen(true)}
                      onCalendarClose={() => setIsDateCalendarOpen(false)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-semibold text-[#14171F] dark:text-white">End Date</Label>
                    <DatePicker
                      value={reportEndDate}
                      onChange={(date) => {
                        if (!date) {
                          setReportEndDate(null);
                          return;
                        }
                        const clamped = date < minDate ? minDate : date > today ? today : date;
                        setReportEndDate(clamped);
                      }}
                      minDate={minDate}
                      maxDate={today}
                      format="MM-dd-y"
                      dayPlaceholder="DD"
                      monthPlaceholder="MM"
                      yearPlaceholder="YYYY"
                      className="w-full dark-date-picker"
                      clearIcon={null}
                      onCalendarOpen={() => setIsDateCalendarOpen(true)}
                      onCalendarClose={() => setIsDateCalendarOpen(false)}
                      calendarIcon={null}
                    />
                  </div>
                </div>
                {dateRangeError && (
                  <p className="mt-3 text-sm text-[#d14343] border border-[#f1b4b4] bg-[#fdeeee] rounded-md px-3 py-2 dark:text-[#f2b8b8] dark:border-[#8b3b3b] dark:bg-[#3a1e1e]">
                    {dateRangeError}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {!isLoadingGenerate && reportStep === "result" && reportGenerationStatus === "ready" && (
          <div className="border-t border-b border-[#E8E9ED] dark:border-darkThemeBorder px-6 py-6 max-h-[60vh] overflow-y-auto">
            <div className="flex flex-col gap-5">
              <div className="bg-[#E8F8EE] border border-[#B6E7C8] rounded-xl p-6 flex flex-col items-center gap-3 dark:bg-[#143022] dark:border-[#1f5a36]">
                <div className="w-14 h-14 rounded-full bg-[#2DB663] flex items-center justify-center text-white dark:bg-[#1f7a46]">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text w-8 h-8 text-white">
                  <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                  <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                  <path d="M10 9H8"></path>
                  <path d="M16 13H8"></path>
                  <path d="M16 17H8"></path>
                </svg>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-[#1F2937] dark:text-white">Report Generated Successfully!</p>
                  <p className="text-sm text-[#4B5563] dark:text-white/70">Your report is ready for download</p>
                </div>
              </div>

              <div className="border border-[#E1E5EA] dark:border-darkThemeBorder rounded-xl p-4 flex flex-col gap-3 text-sm text-[#1F2937] dark:text-white">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Zone:</span>
                  <span>{getSelectedFleetName()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Period:</span>
                  <span>{formatPeriod()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Report Scope:</span>
                  <span>{reportType === "ZONE_WIDE" ? "Zone-Wide" : "Unit-Specific"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Report Kind:</span>
                  <span data-testid="generate-report-modal-summary-kind">
                    {reportKind === ReportKind.alerts ? "Alerts" : "Data"}
                  </span>
                </div>
                {reportKind === ReportKind.data && (
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-semibold shrink-0">Fields:</span>
                    <span className="text-right" data-testid="generate-report-modal-summary-fields">
                      {selectedFields
                        .map((id) => DATA_REPORT_FIELDS.find((field) => field.id === id)?.label || id)
                        .join(", ")}
                    </span>
                  </div>
                )}
                {reportType === "UNIT_SPECIFIC" && (
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Units Selected:</span>
                    <span>{selectedReportUnits.length}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 w-full">
                <Button
                  onClick={handleDownloadReport}
                  disabled={downloadButtonDisabled}
                  data-testid="generate-report-modal-download"
                  className={`bg-[#FF8C65] hover:bg-[#f5784d] text-white font-semibold px-6 h-11 rounded-md flex items-center justify-center gap-2 w-full ${
                    downloadButtonDisabled ? "opacity-60 pointer-events-none" : ""
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Download
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {!isLoadingGenerate && (
          <div className="px-6 py-5 flex flex-col gap-4 bg-[#F7F9FB] dark:bg-darkThemeModalBg sm:flex-row sm:items-center sm:justify-between">
            {reportGenerationStatus === "ready" && reportStep === "result" ? (
              <div className="w-full flex flex-col sm:flex-row gap-4 items-center justify-end">
                <Button
                  onClick={() => {
                    onClose();
                    setReportGenerationStatus("idle");
                    setReportDownloadUrl(null);
                    if (reportGenerationTimeoutRef.current) {
                      clearTimeout(reportGenerationTimeoutRef.current);
                    }
                  }}
                  data-testid="generate-report-modal-done"
                  className="bg-[#2F3643] hover:bg-[#242b36] text-white font-semibold px-6 h-11 rounded-md flex items-center justify-center w-full sm:w-auto"
                >
                  Close
                </Button>
              </div>
            ) : (
              <>
                <Button
                  data-testid="generate-report-modal-back"
                  className={`flex w-full items-center gap-2 text-sm font-semibold sm:w-auto ${
                    currentStepIndex === 0 || reportGenerationStatus === "loading"
                      ? "text-[#A5A9B1] cursor-not-allowed"
                      : "text-[#4B4F55] dark:text-white hover:text-[#14171F]"
                  }`}
                  onClick={currentStepIndex === 0 || reportGenerationStatus === "loading" ? undefined : handleReportBack}
                  disabled={currentStepIndex === 0 || reportGenerationStatus === "loading"}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.5 12L5.5 8L9.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Back
                </Button>
                <div className="flex w-full items-center justify-center gap-2 sm:w-auto sm:justify-start">
                  {Array.from({ length: totalSteps }, (_, dot) => dot).map((dot) => (
                    <span key={dot} className={`h-2 w-2 rounded-full ${dot === currentStepIndex ? "bg-[#ff8c65]" : "bg-[#D4D6DA]"}`} />
                  ))}
                </div>
                <Button
                  onClick={handleReportNext}
                  disabled={isNextDisabled}
                  data-testid="generate-report-modal-next"
                  className={`flex w-full items-center gap-2 px-5 h-10 rounded-md text-base font-semibold leading-5 sm:w-auto ${
                    isNextDisabled
                      ? "bg-[#F0F2F5] text-[#A5A9B1] cursor-not-allowed"
                      : "bg-[#FF8C65] hover:bg-[#f5784d] text-white"
                  }`}
                >
                  {reportStep === "dateRange" ? "Generate Report" : "Next"}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={isNextDisabled ? "opacity-70" : ""}
                  >
                    <path d="M6.5 4L10.5 8L6.5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateReportModal;
